/**
 * Helpers partagés — thème CALCUL LITTÉRAL 4ème (attendus 158-163, desc 12).
 *
 * Moteur d'expressions/équations affines EXACT : arithmétique rationnelle
 * entière (aucun flottant). Deux familles de formatage :
 *   · texte brut (fmtPoly, fmtAffine, fmtMembre, tete, coeffL) : moins
 *     typographique « − » (U+2212), historique, gardé tel quel pour les gens
 *     qui l'importent encore ;
 *   · fragments KaTeX (texNombre, texPoly, texAffine, revue 2026-09-23) :
 *     moins ASCII, coefficients 1 et −1 masqués, terme de tête négatif GROUPÉ,
 *     à passer dans tex() de generators/utils.js (règle « maths en KaTeX
 *     partout, la lettre x comprise »).
 * Les RÉPONSES (mots des trous, bonne_reponse) restent en texte brut, le signe
 * négatif en « - » ASCII : c'est ce que l'élève tape. Une ligne de phrase à
 * trous peut être entièrement en KaTeX (mode global, gen_149) ; le mot du trou,
 * lui, ne contient jamais de KaTeX.
 *
 * Aucun solveur test-side ne couvre les gens 158 à 163 (solvers/index.js ne
 * route que les automatismes 1106 à 1111) : leur preuve est la vérification
 * par un second chemin dans chaque gen (throw) et les tests jetables de revue.
 */

export const MOINS = '−';               // U+2212, affichage seulement
export const LETTRES = ['x', 'a', 't', 'n'];

// ── Rationnels exacts (signe au numérateur, q > 0, toujours réduit) ──────
export const pgcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
export const rat = (p, q = 1) => { if (q < 0) { p = -p; q = -q; } const g = pgcd(p, q); return { p: p / g, q: q / g }; };
export const ratEq = (a, b) => a.p * b.q === b.p * a.q;

/**
 * Résout a·x + b = c·x + d → rationnel exact, ou null si a === c (pas une
 * équation du 1er degré à solution unique).
 */
export const solveAffine = (a, b, c, d) => (a === c ? null : rat(d - b, a - c));

/** Affiche un rationnel : { 5,1 } → « 5 » ; { -5,13 } → « -5/13 » (signe ASCII). */
export const fmtRat = (r) => (r.q === 1 ? String(r.p) : `${r.p}/${r.q}`);

// ── Formatage de polynômes (descendant : x², x, constante) ───────────────

/**
 * Rend une liste de termes [coef, suffixe] en cachant les coefficients nuls et
 * les coefficients 1/−1 devant une lettre. Signes typographiques « − ».
 *   fmtPoly([[3,'x'],[5,'']])      → « 3x + 5 »
 *   fmtPoly([[24,'x²'],[12,'x']])  → « 24x² + 12x »
 *   fmtPoly([[-1,'x'],[-3,'']])    → « −x − 3 »
 *   fmtPoly([[0,'x'],[7,'']])      → « 7 »
 */
export const fmtPoly = (termes) => {
    const nz = termes.filter(([c]) => c !== 0);
    if (nz.length === 0) return '0';
    let out = '';
    nz.forEach(([c, suf], i) => {
        const abs = Math.abs(c);
        const body = (abs === 1 && suf !== '') ? suf : `${abs}${suf}`;
        if (i === 0) out += (c < 0 ? MOINS : '') + body;
        else out += (c < 0 ? ` ${MOINS} ` : ' + ') + body;
    });
    return out;
};

/** ax + b en texte. */
export const fmtAffine = (a, b, L = 'x') => fmtPoly([[a, L], [b, '']]);

/** Membre d'équation ax + b (peut être une constante si a = 0). */
export const fmtMembre = (a, b, L = 'x') => fmtPoly([[a, L], [b, '']]);

/** Constante signée en tête : tete(-4) → « −4 », tete(4) → « 4 ». */
export const tete = (n) => (n < 0 ? `${MOINS}${Math.abs(n)}` : `${n}`);

/** Coefficient devant une lettre, 1/−1 cachés : coeffL(-1,'x') → « −x ». */
export const coeffL = (a, L) => {
    if (a === 0) return '';
    const s = a < 0 ? MOINS : '';
    const abs = Math.abs(a);
    return abs === 1 ? `${s}${L}` : `${s}${abs}${L}`;
};

// ── Fragments KaTeX (revue 2026-09-23) ───────────────────────────────────
// Sans les « $ » : à composer puis à passer dans tex(). Pourquoi GROUPER le
// moins de tête : tex() préfixe « \mathrm{} », un atome ordinaire ; un « - »
// qui le suit serait lu comme une SOUSTRACTION (espaces d'opérateur binaire,
// « − 3x » décalé). Entre accolades, « {-3} » est un nombre négatif. Après
// « = », « ( » ou un opérateur, le moins est déjà unaire : les accolades n'y
// changent rien, on les garde pour n'avoir qu'une règle.

/** Nombre entier en KaTeX : texNombre(-4) → « {-4} », texNombre(4) → « 4 ». */
export const texNombre = (n) => (n < 0 ? `{-${Math.abs(n)}}` : `${n}`);

/**
 * Liste de termes [coef, suffixe LaTeX] en KaTeX : coefficients nuls cachés,
 * 1 et −1 masqués devant une lettre, moins ASCII, tête négative groupée.
 *   texPoly([[3, 'x'], [5, '']])       → « 3x + 5 »
 *   texPoly([[-3, 'x'], [5, '']])      → « {-3}x + 5 »
 *   texPoly([[-1, 'x'], [-3, '']])     → « {-x} - 3 »
 *   texPoly([[1, 'x^2'], [-9, '']])    → « x^2 - 9 »
 *   texPoly([[0, 'x'], [-7, '']])      → « {-7} »
 *   texPoly([[0, 'x'], [0, '']])       → « 0 »
 */
export const texPoly = (termes) => {
    const nz = termes.filter(([c]) => c !== 0);
    if (nz.length === 0) return '0';
    return nz.map(([c, suf], i) => {
        const abs = Math.abs(c);
        const lettreSeule = abs === 1 && suf !== '';
        if (i === 0) {
            if (c > 0) return lettreSeule ? suf : `${abs}${suf}`;
            return lettreSeule ? `{-${suf}}` : `{-${abs}}${suf}`;
        }
        return `${c < 0 ? ' - ' : ' + '}${lettreSeule ? suf : `${abs}${suf}`}`;
    }).join('');
};

/** ax + b en KaTeX : texAffine(-1, -4) → « {-x} - 4 » ; texAffine(3, 0) → « 3x ». */
export const texAffine = (a, b, L = 'x') => texPoly([[a, L], [b, '']]);

// ── Nature d'une expression (dernière opération, niveau 0 de parenthèses) ──
// (chemin déclaratif utilisé côté gen pour construire des familles honnêtes ;
//  aucun solveur test-side n'en a d'implémentation pour 158-163)
export const natureCons = (expr) => {
    const e = String(expr).replace(/−/g, '-');
    let depth = 0, somme = false, quotient = false;
    for (let i = 0; i < e.length; i++) {
        const ch = e[i];
        if (ch === '(') depth += 1;
        else if (ch === ')') depth -= 1;
        else if (depth === 0) {
            if (ch === '+' || (ch === '-' && i > 0 && e[i - 1] === ' ')) somme = true;
            else if (ch === '/') quotient = true;
        }
    }
    if (somme) return 'somme';
    if (quotient) return 'quotient';
    return 'produit';
};
